import { initializeApp } from "firebase/app";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  getIdToken,
  getAuth,
  GoogleAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  linkWithCredential,
} from "firebase/auth";


const app = initializeApp({
  apiKey: process.env.REACT_APP_AUTHENTICATION_API_KEY,
  authDomain: process.env.REACT_APP_AUTHENTICATION_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_AUTHENTICATION_PROJECT_ID,
  storageBucket: process.env.REACT_APP_AUTHENTICATION_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_AUTHENTICATION_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_AUTHENTICATION_APP_ID,
  measurementId: process.env.REACT_APP_AUTHENTICATION_MEASUREMENT_ID

});

export const auth = getAuth(app);

export const getToken = (user) => user ? getIdToken(user) : null;
export const onAuthStateChange = (callback) => onAuthStateChanged(auth, callback);
export const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const register = (email, password) => createUserWithEmailAndPassword(auth, email, password);
export const logout = () => signOut(auth);

export const getUser = () => auth.currentUser;

export const PASSWORD_PROVIDER_ID = EmailAuthProvider.PROVIDER_ID;

export const hasPasswordProvider = (user = auth.currentUser) =>
  Boolean(
    user?.providerData?.some(
      (provider) => provider.providerId === PASSWORD_PROVIDER_ID,
    ),
  );

const linkPasswordError = (error) => {
  switch (error?.code) {
    case "auth/email-already-in-use":
      return "This email already has a password account. Sign in with that email on mobile, or contact support if the accounts need merging.";
    case "auth/requires-recent-login":
      return "Please sign out, sign back in with Google, then enable email sign-in again.";
    case "auth/weak-password":
      return "Choose a stronger password (at least 6 characters).";
    case "auth/provider-already-linked":
      return "Email and password sign-in is already enabled.";
    case "auth/invalid-email":
      return "This account needs a valid email before a password can be added.";
    default:
      return error?.message || "Unable to enable email sign-in.";
  }
};

export const linkEmailPassword = async (email, password) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You need to be signed in first.");
  if (!email) throw new Error("This account does not have an email to link.");

  try {
    const { user: linkedUser } = await linkWithCredential(
      user,
      EmailAuthProvider.credential(email, password),
    );
    return linkedUser;
  } catch (error) {
    throw new Error(linkPasswordError(error));
  }
};

export const providerBasedAuthentication = async (provider) => {
  switch (provider) {
    case "google":
      try {
        const provider = new GoogleAuthProvider();
        const { user } = await signInWithPopup(auth, provider);
        return user;
      } catch (error) {
        if (error.code === "auth/popup-closed-by-user") {
          throw new Error("Popup closed by user");
        }
        if (error.code === "auth/popup-blocked") {
          const { user } = await signInWithRedirect(auth, provider);
          return user;

        }
        console.log(error);
        throw new Error("Failed to login with provider");
      }


    default:
      throw new Error("Invalid provider");
  }
}